/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import {
  CommentContentUpdateArgs,
  CommentDeleteArgs,
  CommentHideArgs,
  CommentPostInStoryCardArgs,
  CommentReactionArgs,
  EventByIdArgs,
  EventDeleteArgs,
  EventUpdateArgs,
  StoryCard,
  StoryCardAddToBookmarksArgs,
  StoryCardCommentsArgs,
  StoryCardDeleteArgs,
  StoryCardPollVoteArgs,
  StoryCardReactionArgs,
  StoryCardRemoveFromBookmarksArgs,
  StoryCardRepostArgs,
  StoryCardUpdateArgs,
  StoryCardsFeedArgs,
  StoryCardsFeedPayload,
  StoryUpdateArgs,
  UserPreview,
  commentApi,
  eventsApi,
  storyApi,
  storyCardApi,
} from '../services';
import { PaginationInfo } from '../shared';

export const getStoryFeedHandler = async (args: StoryCardsFeedArgs, { dispatch }: any) => {
  if (!args.storyId) {
    return {
      items: [],
      pageInfo: { page: 0, hasNextPage: false } as PaginationInfo,
    } as StoryCardsFeedPayload;
  }

  const storyCardsFeed = await dispatch(
    storyCardApi.endpoints.storyCardsFeed.initiate({ ...args })
  ).unwrap();

  return { ...args, ...storyCardsFeed };
};

export const updateStoryHandler = async (args: StoryUpdateArgs, { dispatch }: any) => {
  return dispatch(storyApi.endpoints.storyUpdate.initiate({ ...args })).unwrap();
};

export const updateCardHandler = async (args: StoryCardUpdateArgs, { dispatch }: any) => {
  return dispatch(storyCardApi.endpoints.storyCardUpdate.initiate({ ...args })).unwrap();
};

export const updateStory = createAsyncThunk('updateStory', updateStoryHandler);

export const togglePublishOnStory = createAsyncThunk('togglePublishOnStory', updateStoryHandler);

export const addComment = createAsyncThunk(
  'addComment',
  async (args: CommentPostInStoryCardArgs, { dispatch }) => {
    const comment = await dispatch(
      commentApi.endpoints.commentPostInStoryCard.initiate(args)
    ).unwrap();
    return { ...args, comment };
  }
);

export const editComment = createAsyncThunk(
  'editComment',
  async (args: CommentContentUpdateArgs, { dispatch }) => {
    const comment = await dispatch(
      commentApi.endpoints.commentContentUpdate.initiate(args)
    ).unwrap();
    return { comment };
  }
);

export const deleteComment = createAsyncThunk(
  'deleteComment',
  async (args: CommentDeleteArgs & { repliesCount: number }, { dispatch }) => {
    const deleted = await dispatch(commentApi.endpoints.commentDelete.initiate(args)).unwrap();
    return { ...args, deleted };
  }
);

export const reactOnComment = createAsyncThunk(
  'reactOnComment',
  async (args: CommentReactionArgs, { dispatch }) => {
    const reactions = await dispatch(commentApi.endpoints.commentReaction.initiate(args)).unwrap();
    return { ...args, reactions };
  }
);

export const hideComment = createAsyncThunk(
  'hideComment',
  async (args: CommentHideArgs, { dispatch }) => {
    const comment = await dispatch(commentApi.endpoints.commentHide.initiate(args)).unwrap();
    return { comment };
  }
);

export const highlightComment = createAsyncThunk(
  'highlightComment',
  async (args: CommentHideArgs, { dispatch }) => {
    const comment = await dispatch(commentApi.endpoints.commentHighlight.initiate(args)).unwrap();
    return { comment };
  }
);

export const reactOnCard = createAsyncThunk(
  'reactOnCard',
  async (args: StoryCardReactionArgs, { dispatch }) => {
    const reactions = await dispatch(
      storyCardApi.endpoints.storyCardReaction.initiate(args)
    ).unwrap();
    return { ...args, reactions };
  }
);

export const loadMoreComments = createAsyncThunk(
  'loadMoreComments',
  async (args: StoryCardCommentsArgs, { dispatch }) => {
    const { items } = await dispatch(
      storyCardApi.endpoints.storyCardComments.initiate(args)
    ).unwrap();
    return { ...args, items };
  }
);

export const addCard = createAction<StoryCard>('addCard');

export const editCard = createAction<StoryCard>('editCard');

export const deleteCard = createAsyncThunk(
  'deleteCard',
  async (args: StoryCardDeleteArgs, { dispatch }) => {
    const deleted = await dispatch(storyCardApi.endpoints.storyCardDelete.initiate(args)).unwrap();
    return { ...args, deleted };
  }
);

export const repostCard = createAsyncThunk(
  'repostCard',
  async (args: StoryCardRepostArgs, { dispatch }) => {
    return await dispatch(storyCardApi.endpoints.storyCardRepost.initiate(args)).unwrap();
  }
);

export const togglePublishOnCard = createAsyncThunk('togglePublishOnCard', updateCardHandler);

export const toggleCardSection = createAsyncThunk('toggleCardSection', updateCardHandler);

export const addCardToBookmarks = createAsyncThunk(
  'addCardToBookmarks',
  async (args: StoryCardAddToBookmarksArgs, { dispatch }) => {
    const payload = await dispatch(
      storyCardApi.endpoints.storyCardAddToBookmarks.initiate(args)
    ).unwrap();
    return { ...args, payload };
  }
);

export const removeCardFromBookmarks = createAsyncThunk(
  'removeCardFromBookmarks',
  async (args: StoryCardRemoveFromBookmarksArgs, { dispatch }) => {
    const payload = await dispatch(
      storyCardApi.endpoints.storyCardRemoveFromBookmarks.initiate(args)
    ).unwrap();
    return { ...args, payload };
  }
);

export const pollVote = createAsyncThunk(
  'pollVote',
  async (args: StoryCardPollVoteArgs, { dispatch }) => {
    const payload = await dispatch(
      storyCardApi.endpoints.storyCardPollVote.initiate(args)
    ).unwrap();
    return { ...args, payload };
  }
);

interface EventParticipationArgs {
  channelId: number;
  eventId: number;
  userProfile: UserPreview; // * used for updating participantsPreview in the store */
}

export const eventCancelParticipation = createAsyncThunk(
  'eventCancelParticipation',
  async ({ userProfile, ...apiArgs }: EventParticipationArgs, { dispatch }) => {
    const payload = await dispatch(
      eventsApi.endpoints.eventCancelParticipation.initiate(apiArgs)
    ).unwrap();
    return { ...apiArgs, payload, userProfile };
  }
);

export const eventJoinAsParticipant = createAsyncThunk(
  'eventJoinAsParticipant',
  async ({ userProfile, ...apiArgs }: EventParticipationArgs, { dispatch }) => {
    const payload = await dispatch(
      eventsApi.endpoints.eventJoinAsParticipant.initiate(apiArgs)
    ).unwrap();
    return { ...apiArgs, payload, userProfile };
  }
);

export const updateEventHandler = async (args: EventUpdateArgs, { dispatch }: any) => {
  return dispatch(eventsApi.endpoints.eventUpdate.initiate(args)).unwrap();
};

export const eventCancel = createAsyncThunk('eventCancel', updateEventHandler);

export const eventEdit = createAsyncThunk('eventEdit', updateEventHandler);

export const eventStatusUpdate = createAsyncThunk('eventStatusUpdate', updateEventHandler);

export const eventUpdateChannels = createAsyncThunk('eventUpdateChannels', updateEventHandler);

export const eventDelete = createAsyncThunk(
  'eventDelete',
  async (args: EventDeleteArgs, { dispatch }) => {
    const deleted = await dispatch(eventsApi.endpoints.eventDelete.initiate(args)).unwrap();
    return { ...args, deleted };
  }
);

export const updateCardEvent = createAsyncThunk(
  'updateCardEvent',
  async (args: EventByIdArgs, { dispatch }) => {
    return await dispatch(eventsApi.endpoints.eventById.initiate(args)).unwrap();
  }
);
